import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import { Avatar, Button, Dropdown, Layout, Menu, message, Popconfirm, Switch, Table, Tag, Tooltip, Upload } from 'antd';
// import { useForm } from 'antd/lib/form/Form';
import { observer, Observer } from 'mobx-react-lite';
import * as React from 'react';
import LayoutHeader from '../../../components/LayoutHeader';
import { LoadingIndicator } from '../../../components/LoadingIndicator';
import { HasPermission } from '../../authorization/components/HasPermission';
import { AppPermissions } from '../../authorization/Permissions';
import { Utils } from '../../common/services/Utils';
import { RulesStores } from '../../rules/stores';
import { IotaAppStores } from '../stores';
import { ApplicationDefinitionBase } from '../types/ApplicationDefinition';
import { TableRow } from '../stores/ProjectApplicationDefinitionsVisualStore';
import WrappedAddApplicationDefinition from './AddApplicationDefinition';
import ApplicationSettingsDialog from './ApplicationSettingsDialog';
import EditIotaApplicationDialog from './EditIotaApplicationDialog';
import { ApiApplicationDialog } from '.';
import './ProjectApplicationDefinitionsList.less';
import { UploadChangeParam } from 'antd/lib/upload';
import RulesImportDialog from '../../rules/screens/RulesImportDialog';
import { endpoints } from '../../../config';

type Props = IotaAppStores & RulesStores;
const { Content } = Layout;

export const ApplicationDefinitionList: React.FC<Props> = ({
    projectApplicationDefinitionsUI: store,
    RulesImportUI: rulesImportStore
}) => {
    const appDefStore = store!;
    const [highlightedAppId, setHighlightedAppId] = React.useState<string | null>(null);

    React.useEffect(() => {
        store!.loadIotaInputsMetadata();
    }, [store]);

    React.useEffect(() => {
        appDefStore.loadApplicationDefinitions();
    }, [appDefStore.currentProject, appDefStore]);

    React.useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const appId = urlParams.get('highlightAppId');

        if (appId && appDefStore.applicationDefinitions.length > 0) {
            setTimeout(() => {
                setHighlightedAppId(appId);

                let element = document.querySelector(`tr[data-row-key="app-${appId}"]`);

                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }

                setTimeout(() => {
                    setHighlightedAppId(null);
                }, 1200);
            }, 600);
        }
    }, [appDefStore.applicationDefinitions]);

    const iotaAppMenu = (appDef: ApplicationDefinitionBase) => {
        return (
            <Menu data-id-type="popup-menu-list-root" data-id-name={`${appDef.name}-table`}>
                <Menu.Item
                    key="edit"
                    data-id-type="popup-menu-list-item"
                    data-id-name="Edit Application"
                    data-id={`iota-application-list-edit-app-${appDef.id}`}
                    onClick={() => store!.handleEditAppClick(appDef.id)}
                >
                    Edit application
                </Menu.Item>
                <Menu.Item
                    key="export"
                    data-id-type="popup-menu-list-item"
                    data-id-name="Export Application"
                    data-id={`iota-application-list-export-app-${appDef.id}`}
                    onClick={() => store!.handleExportAppClick(appDef.id)}
                >
                    Export
                </Menu.Item>
                <Menu.Item key="delete" data-id-type="popup-menu-list-item" data-id-name="Delete" danger>
                    <Popconfirm
                        title="Are you sure that you want to delete this application definition?"
                        okText="Yes"
                        onConfirm={() => deleteAppDefinition(appDef.id)}
                        cancelText="No"
                        okButtonProps={{ id: `iota-application-list-delete-confirm-${appDef.id}` }}
                        cancelButtonProps={{ id: `iota-application-list-delete-cancel-${appDef.id}` }}
                    >
                        <div>Delete</div>
                    </Popconfirm>
                </Menu.Item>
            </Menu>
        );
    };

    const columns = [
        {
            title: 'Name',
            key: 'name',
            sorter: false,
            render: (row: TableRow) => {
                if (row.type === 'group') {
                    const isCollapsed = store!.collapsedGroups.has(row.id);
                    const Icon = isCollapsed ? CaretRightOutlined : CaretDownOutlined;

                    return {
                        children: (
                            <div className="group-title-wrapper" onClick={() => store!.toggleGroupCollapse(row.id)}>
                                <Icon style={{ fontSize: '12px' }} />
                                <div>
                                    <div className="group-title">Group: {row.name}</div>
                                </div>
                            </div>
                        ),
                        props: {
                            colSpan: 4,
                            'data-id-value': row.name,
                            'data-id-type': 'group-list',
                            'data-id-cells': 'Group'
                        }
                    };
                }

                const appDef = row.data as ApplicationDefinitionBase;
                return {
                    children: (
                        <div className="app-title-wrapper">
                            <Avatar shape="square" size="small" src={appDef.iconUrl} style={{ marginRight: '12px' }} />
                            <div>
                                <div className="app-title">{appDef.name}</div>
                                <div className="app-subtitle">
                                    {store!.getWorkflowName(appDef.extension, appDef.applicationId, appDef.workflowId)}
                                </div>
                            </div>
                        </div>
                    ),
                    props: {
                        'data-id-value': appDef.name,
                        'data-id-type': 'applications-list',
                        'data-id-cells': 'Name'
                    }
                };
            }
        },
        {
            title: 'Description',
            key: 'description',
            sorter: false,
            render: (row: TableRow) => {
                if (row.type === 'group') {
                    return {
                        children: null,
                        props: { colSpan: 0 }
                    };
                }

                const appDef = row.data as ApplicationDefinitionBase;
                return {
                    children: <span className="app-description">{appDef.description || '-'}</span>,
                    props: {
                        'data-id-value': appDef.description || '',
                        'data-id-type': 'applications-list',
                        'data-id-cells': 'Description'
                    }
                };
            }
        },
        {
            title: 'State',
            dataIndex: 'state',
            key: 'state',
            sorter: false,
            render: (state: string, row: TableRow) => {
                if (row.type === 'group') {
                    return {
                        children: null,
                        props: { colSpan: 0 }
                    };
                }

                const appDef = row.data as ApplicationDefinitionBase;
                return {
                    children: (
                        <Tag className="alpha-portal-tag" color={appDef.state === 'Draft' ? '#9BA0AA' : '#1B5E20'}>
                            {appDef.state}
                        </Tag>
                    ),
                    props: {
                        'data-id-value': appDef.state,
                        'data-id-type': 'applications-list',
                        'data-id-cells': 'State'
                    }
                };
            }
        },
        {
            title: 'Actions',
            width: '280px',
            key: 'actions',
            render: (row: TableRow) => {
                if (row.type === 'group') {
                    return {
                        children: null,
                        props: { colSpan: 0 }
                    };
                }

                const appDef = row.data as ApplicationDefinitionBase;
                return (
                    <div className="operation-label-container" style={{ textAlign: 'right' }}>
                        <HasPermission
                            entityId={store?.currentProject?.id}
                            permissionClaim={AppPermissions.CanEditIotaApplications}
                            yes={() => (
                                <div className="actions-container">
                                    <div className="table-operation">
                                        <Button
                                            size="small"
                                            type="link"
                                            data-id-cells="Edit inputs"
                                            data-id-name={appDef.name}
                                            onClick={() => store!.editInputs(appDef.id)}
                                        >
                                            <Tooltip title="Edit inputs">
                                                <i className="alpha-icon lg input-icon" />
                                            </Tooltip>
                                        </Button>
                                        <Button
                                            size="small"
                                            type="link"
                                            data-id-cells="Edit settings"
                                            data-id-name={appDef.name}
                                            onClick={() => navigateToSettings(appDef)}
                                        >
                                            <Tooltip title="Edit settings">
                                                <i className="alpha-icon lg gear-gray-icon" />
                                            </Tooltip>
                                        </Button>
                                        <Dropdown
                                            overlay={() => iotaAppMenu(appDef)}
                                            trigger={['click']}
                                            overlayClassName="alpha-dropdown-menu"
                                        >
                                            <Button
                                                type="link"
                                                data-id-cells="More"
                                                data-id-name={appDef.name}
                                                size="small"
                                            >
                                                <i className="alpha-icon lg more-icon" />
                                            </Button>
                                        </Dropdown>
                                    </div>
                                    <Switch
                                        data-id={`iota-application-list-switch-${appDef.id}`}
                                        checked={appDef.state === 'Published'}
                                        onChange={() => updateApplicationState(appDef)}
                                        disabled={
                                            store!.applicationDefinitions.find(a => a.id === appDef.id)?.state ===
                                            'Published'
                                                ? false
                                                : !store!.validateSettingsAndInputs(appDef)
                                        }
                                    />
                                </div>
                            )}
                            no={() => (
                                <div className="table-operation">
                                    <Button
                                        size="small"
                                        type="link"
                                        data-id={`iota-application-list-inputs-${appDef.id}`}
                                        onClick={() => store!.editInputs(appDef.id)}
                                    >
                                        <Tooltip title="See inputs">
                                            <i className="alpha-icon lg input-icon" />
                                        </Tooltip>
                                    </Button>
                                </div>
                            )}
                        />
                    </div>
                );
            }
        }
    ];

    const navigateToSettings = (appDef: ApplicationDefinitionBase) => {
        store!.goToSettingsPage(appDef);
    };

    const deleteAppDefinition = async (appDefId: string) => {
        await store!.deleteApplicationDefinition(appDefId);
    };

    const updateApplicationState = async (appDef: ApplicationDefinitionBase) => {
        const newState = appDef.state === 'Draft' ? 'Published' : 'Draft';
        await store!.updateApplicationState(appDef.id, newState);
    };

    const onChange = (info: UploadChangeParam) => {
        const status = info.file.status;
        if (status === 'done') {
            const rulesResponse = info.file.response;
            rulesImportStore!.setTableDataForIotaImport(rulesResponse);
            message.success(`${info.file.name} file uploaded successfully.`);
        } else if (status === 'error') {
            if (info.file?.response?.status === 409) {
                message.warning(info.file.response.title);
                return;
            }
            message.error(`${info.file.name} file upload failed.`);
        }
    };

    const importCallback = async () => {
        const promises = [store!.loadApplicationDefinitions()];
        await Promise.all(promises);
    };

    if (store!.isLoading || !store!.currentProject) {
        return <LoadingIndicator />;
    }

    const layout = (
        <Layout className="screen-size" style={{ ...{ height: '100%', background: 'white' } }}>
            <LayoutHeader
                subtitle={Utils.getSubtitle(appDefStore!.currentProject)}
                title="IOTA Apps"
                buttons={[
                    <HasPermission
                        key="rules-import"
                        entityId={store?.currentProject?.id}
                        permissionClaim={AppPermissions.CanEditIotaApplications}
                    >
                        <Upload
                            showUploadList={false}
                            className="headerButton"
                            key="file-uploader"
                            name="file"
                            onChange={(info: UploadChangeParam) => onChange(info)}
                            action={`${endpoints.manageUrl}projects/${store!.currentProject!.id}/iota/applications/import`}
                            headers={store!.fileImportActionHeaders}
                            beforeUpload={() => store!.setHeaders()}
                        >
                            <span data-id="button-Import" key="import-button-container">
                                <i className="alpha-icon xs arrow-down" style={{ verticalAlign: 'middle' }} />
                                <span style={{ marginLeft: 11 }}>Import</span>
                            </span>
                        </Upload>
                    </HasPermission>,
                    <span key="help-message" className="tooltip-container">
                        <Tooltip title="Configure field bindings in the Inputs section, while Settings manage the connection to external databases.">
                            <i className="alpha-icon lg question-icon" style={{ verticalAlign: 'middle' }} />
                        </Tooltip>
                    </span>,
                    <HasPermission
                        key="add-app-def"
                        entityId={store?.currentProject?.id}
                        permissionClaim={AppPermissions.CanEditIotaApplications}
                        yes={() => (
                            <Button
                                type="primary"
                                size="large"
                                data-id="iota-application-list-add-definition"
                                onClick={() => store!.handleAddApplicationDefinition()}
                            >
                                Add App Definition
                            </Button>
                        )}
                    />,
                    <HasPermission
                        key="add-api-app"
                        entityId={store?.currentProject?.id}
                        permissionClaim={AppPermissions.CanEditIotaApplications}
                        yes={() => (
                            <Button
                                type="primary"
                                size="large"
                                data-id="iota-application-list-add-api-app"
                                onClick={() => store!.handleAddApiApplication()}
                            >
                                Add API app
                            </Button>
                        )}
                    />
                ]}
            />
            <Content data-id="iota-application-list-container" style={{ background: 'white', padding: '0 24px' }}>
                <WrappedAddApplicationDefinition projectApplicationDefinitionsUI={store} />
                <RulesImportDialog importCallback={importCallback} />
                <ApplicationSettingsDialog store={store} />
                <EditIotaApplicationDialog store={store} />
                <ApiApplicationDialog store={store!} />
                <Observer>
                    {() => (
                        <Table
                            className="alpha-portal-table iota-application-list-table"
                            dataSource={appDefStore.flatTableData}
                            columns={columns}
                            rowKey={row => row.id}
                            rowClassName={row => {
                                const baseClass = row.type === 'group' ? 'iota-app-group-row' : 'iota-app-row';
                                const isHighlighted =
                                    row.type === 'application' &&
                                    row.data &&
                                    (row.data as ApplicationDefinitionBase).id === highlightedAppId;

                                return isHighlighted ? `${baseClass} highlight-row` : baseClass;
                            }}
                            pagination={false}
                            showHeader
                            bordered={false}
                        />
                    )}
                </Observer>
            </Content>
        </Layout>
    );

    return layout;
};

export default observer(ApplicationDefinitionList);
